import { Component, EventEmitter, Input, Output, OnInit } from '@angular/core';
import { Customers, CustomerCreate, CustomerResponse } from '../../../interfaces';
import { DataServiceService } from '../../../services/data-service.service';
import { MessageService } from 'primeng/api';

@Component({
  selector: 'app-customer-form',
  templateUrl: './customer-form.component.html',
  styleUrl: './customer-form.component.scss'
})
export class CustomerFormComponent implements OnInit {
  @Input() displayAddDialog = false;
  @Input() availableCustomers: Customers[] = [];
  
  @Output() displayAddDialogChange = new EventEmitter<boolean>();
  @Output() customerSaved = new EventEmitter<void>();
  
  newCustomer: any = {};
  saving = false;

  customerTypeOptions = [
    { label: 'Both', value: 'Both' },
    { label: 'Receiver', value: 'Receiver' },
    { label: 'Supplier', value: 'Supplier' }
  ];

  constructor(
    private dataServiceService: DataServiceService,
    private messageService: MessageService
  ) {}

  get shouldShowRelationships(): boolean {
    return this.newCustomer.customer_type === 'Supplier' || 
           this.newCustomer.customer_type === 'Receiver' || 
           this.newCustomer.customer_type === 'Both';
  }

  get availableSuppliersForRelationship(): Customers[] {
    return this.availableCustomers.filter(customer => 
      customer.customer_id !== this.newCustomer.customer_id
    );
  }

  get availableReceiversForRelationship(): Customers[] {
    return this.availableCustomers.filter(customer => 
      customer.customer_id !== this.newCustomer.customer_id
    );
  }

  ngOnInit() {
    this.resetNewCustomerForm();
  }

  closeDialog() {
    this.displayAddDialog = false;
    this.displayAddDialogChange.emit(false);
    this.resetNewCustomerForm();
  }

  saveNewCustomer() {
    // Validate required fields
    if (!this.newCustomer.name || !this.newCustomer.customer_type) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Customer name and type are required'
      });
      return;
    }

    this.saving = true;
    const customerToCreate: CustomerCreate = {
      name: this.newCustomer.name?.trim(),
      status: this.newCustomer.status || 'Active',
      customer_type: this.newCustomer.customer_type,
      contact_person: this.newCustomer.contact_person?.trim() || undefined,
      email: this.newCustomer.email?.trim() || undefined,
      phone: this.newCustomer.phone?.trim() || undefined,
      address: this.newCustomer.address?.trim() || undefined,
      city: this.newCustomer.city?.trim() || undefined,
      state: this.newCustomer.state?.trim() || undefined,
      zipcode: this.newCustomer.zipcode?.trim() || undefined,
      notes: this.newCustomer.notes?.trim() || '',
      service_hours_start: this.newCustomer.service_hours_start?.trim() || null,
      service_hours_end: this.newCustomer.service_hours_end?.trim() || null,
      delivers_to: this.newCustomer.delivers_to || [],
      receives_from: this.newCustomer.receives_from || []
    };

    this.dataServiceService.createCustomer(customerToCreate).subscribe({
      next: (response: CustomerResponse) => {
        this.saving = false;
        this.closeDialog();
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Customer added successfully'
        });
        
        // Emit event to parent to refresh data
        this.customerSaved.emit();
      },
      error: (error) => {
        this.saving = false;
        console.error('Customer creation error:', error);
        let errorMessage = 'Failed to add customer';

        if (error.error?.detail) {
          errorMessage = error.error.detail;
        }

        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: errorMessage
        });
      }
    });
  }

  private resetNewCustomerForm() {
    this.newCustomer = {
      name: '',
      status: 'Active',
      customer_type: '',
      contact_person: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      zipcode: '',
      notes: '',
      service_hours_start: null,
      service_hours_end: null,
      delivers_to: [],
      receives_from: []
    };
  }

  onCustomerTypeChange() {
    // Clear relationship selections when customer type changes
    if (!this.shouldShowRelationships) {
      this.newCustomer.delivers_to = [];
      this.newCustomer.receives_from = [];
    }
  }
}
